import { of as observableOf, Observable, Subject, Subscription, firstValueFrom } from 'rxjs';

import { filter, tap ,  debounceTime, switchMap } from 'rxjs/operators';
import { Component, EventEmitter, Input, OnChanges, OnInit, Optional, Output, SimpleChanges } from '@angular/core';
import { NgForm } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { CustomComponent, EMAIL_PATTERN, IInvoiceInfo, IUserCompany, ngHelper, paymentHelper } from '@cakeiteasy/web-utils';
import { ToastrService } from 'ngx-toastr';

import { INVOICE_SEND_FORMATS, PAYMENT_PLATFORM_TYPES } from '../../payment.constants';
import { UserManager } from '../../../users/services/user.manager';
import { IPaymentInfo } from '../../models/i-payment-models';
import { IUser } from '../../../users';
import { PaymentStorageService, KEEP_INFO_TYPES } from '../../services/payment-storage.service';

@Component({
  standalone: false,
  selector: 'user-invoice-info',
  templateUrl: './user-invoice-info.component.html',
  styleUrls: ['./user-invoice-info.component.scss'],
})
export class UserInvoiceInfoComponent extends CustomComponent implements OnInit, OnChanges {
  @Input() user: IUser;
  @Input() invoiceInfo: IInvoiceInfo;
  @Input() paymentInfo: IPaymentInfo;
  @Input() countryCode: string;
  @Input() bakeryId: number = null;

  @Input() allowToChangeCountry = true;
  @Input() isForPayment = false;
  @Input() showRemove = false;
  @Input() showInvoiceFormat = true;
  @Input() isSharedBusinessAccount: boolean;
  @Input() invoiceRefRequired = true;
  // uses to stop processing of company
  @Input() companyChangeActions: (company?: IUserCompany) => Promise<any> = null;

  @Input()
  public get isValid(): boolean {
    return this._isInfoValid;
  }

  public set isValid(newValue) {
    this._isInfoValid = newValue;
    this.isValidChange.emit(newValue);
  }

  @Output() onInvoiceUpdated = new EventEmitter<IInvoiceInfo>();
  @Output() onChangeCompanyClick = new EventEmitter<void>();
  @Output() onCompanyChosen = new EventEmitter<IUserCompany>();
  @Output() isValidChange = new EventEmitter<boolean>();

  public EMAIL_PATTERN: RegExp = EMAIL_PATTERN;

  public lookingForAnotherCompany = false;
  public invoiceForm: NgForm = null;
  public formChangeSub: Subscription = null;

  public FORMATS = [];

  public savingRequestsQueue = new Subject<IInvoiceInfo>();

  private _isInfoValid = false;

  constructor(@Optional() private _manager: UserManager,
              private _toast: ToastrService,
              private _paymentStorage: PaymentStorageService,
              private _translate: TranslateService) {
    super();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.showInvoiceFormat = this.showInvoiceFormat && !this.isSharedBusinessAccount;

    ngHelper.ifChanged(changes, 'invoiceInfo', (newVal, oldVal) => {
      if (oldVal === null && newVal) {
        this.invoiceInfo = { ...newVal };
      }

      this.checkEHFInvoiceFormatAvailability();

      if (!newVal) {
        this.invoiceForm = null;
      }
    });

    ngHelper.ifChanged(changes, 'user', (user, prevUser) => {
      if (!user || !user.id || !user.invoiceInfo) {
        return;
      }

      if (!prevUser || prevUser.id !== user.id) {

        if (this.isSharedBusinessAccount) {
          const contactInfo = this._paymentStorage.getContactInfo(PAYMENT_PLATFORM_TYPES.invoice);
          const savedInvoiceInfo = this._paymentStorage.getInvoiceInfo();

          this.invoiceInfo = {
            format: user.invoiceInfo.format,
            email: user.invoiceInfo.email,
            company: { ...(user.invoiceInfo.company || this.invoiceInfo.company) },
            contactPersonName: contactInfo && contactInfo.userName,
            orgName: user.invoiceInfo.orgName,
            orgNumber: user.invoiceInfo.orgNumber,
            reference: savedInvoiceInfo && savedInvoiceInfo.reference
          };
        } else {
          this.invoiceInfo = this.invoiceInfo && this.invoiceInfo.company
            ? this.invoiceInfo
            : user.invoiceInfo;
        }

        setTimeout(() => {
          this.onInvoiceUpdated.emit(this.invoiceInfo);
        });
      }

      this.checkEHFInvoiceFormatAvailability();
    });
  }

  public ngOnInit() {
    this.addSubscriber(this.savingRequestsQueue.pipe(
      filter(() => this.invoiceForm && this.invoiceForm.valid),
      debounceTime(350),
      switchMap((invoiceInfoData) => {
        if (this.user && this.user.id && !this.isSharedBusinessAccount) {
          return firstValueFrom(this._saveInvoiceInfo(invoiceInfoData))
            .then((invoiceInfo) => this.invoiceInfo.id = invoiceInfo.id)
            .catch((err) => this._toast.error(this._translate.instant('notifications.error.userInvoiceInfoSaving.message')));
        }

        return observableOf(null);
      }))
      .subscribe(() => {
        this.onInvoiceUpdated.emit(this.invoiceInfo);
      }));
  }

  public onInvoiceFormatUpdated(newFormat) {
    if (!newFormat || newFormat.id === this.currentInvoiceFormat?.id) {
      return;
    }

    this.invoiceInfo.format = newFormat.id;
    this.savingRequestsQueue.next(this.invoiceInfo);
  }

  public checkEHFInvoiceFormatAvailability() {
    const countryFormatIds = paymentHelper.getInvoiceFormatsForCountry();
    this.FORMATS = countryFormatIds.map(id => ({
      id,
      label: this._translate.instant('common.invoiceFormats.' + id)
    }));

    if (this.invoiceInfo && !this.currentInvoiceFormat) {
      this.invoiceInfo.format = INVOICE_SEND_FORMATS.EHF;
    }
  }

  public get currentInvoiceFormat() {
    return this.invoiceInfo
      ? this.FORMATS.find((format) => format.id === this.invoiceInfo.format)
      : this.FORMATS[0];
  }

  public set currentInvoiceFormat(format) {
    this.invoiceInfo.format = format.id;
  }

  public isEhf(): boolean {
    return this.invoiceInfo && this.invoiceInfo.format === INVOICE_SEND_FORMATS.EHF;
  }

  public registerForm(form: NgForm) {
    if (!this.invoiceForm) {
      this.formChangeSub = form.valueChanges.subscribe(() => {
        this.isValid = form.valid;

        if (this.invoiceInfo && this.isValid && (!form.pristine || !this.invoiceInfo.id)) {
          // necessary to get data from form because this.invoiceInfo is not updated yet, event occur after model updating
          const payload = { ...this.invoiceInfo, ...form.value };
          this.savingRequestsQueue.next(payload);
        }
      });

      this.addSubscriber(this.formChangeSub);
    }

    this.invoiceForm = form;
  }

  public changeCompany() {
    this.onChangeCompanyClick.emit();
    this.isValid = false;
    this.invoiceForm = null;

    if (this.formChangeSub) {
      this.formChangeSub.unsubscribe();
    }

    this.lookingForAnotherCompany = true;
  }

  public async continueWithCompany(company) {
    let info = this._getInvoiceInfoFromCompany(company);

    if (this.user && this.user.id) {
      if (this.invoiceInfo && this.invoiceInfo.company.id !== company.id) {
        await this.removeInvoiceInfo();
      } else {
        info = { ...info, id: this.invoiceInfo && this.invoiceInfo.id || null };
      }

      info = await firstValueFrom(this._saveInvoiceInfo(info));
    }

    this.invoiceInfo = { ...this.invoiceInfo, ...info };

    if (this.companyChangeActions) {
      await this.companyChangeActions(company);
      this._paymentStorage.setInvoiceInfo(this.invoiceInfo);
    }

    this.onCompanyChosen.emit(company);
    this.lookingForAnotherCompany = false;
  }

  public removeInvoiceInfo() {
    let pRemove = Promise.resolve();
    if (this.user && this.invoiceInfo.id) {
      pRemove = this._manager.removeInvoiceInfo().then(
        () => this._paymentStorage.removeInfo(PAYMENT_PLATFORM_TYPES.invoice, KEEP_INFO_TYPES.INVOICE_INFO)
      );
    }

    return pRemove.then(() => {
      this.invoiceForm = null;
      this.invoiceInfo = null;
    });
  }

  private _getInvoiceInfoFromCompany(company): IInvoiceInfo {
    return {
      company: company,
      type: 'COMPANY',
      format: INVOICE_SEND_FORMATS.EHF,
      orgNumber: company.legalCompany.orgNumber,
      orgName: company.legalCompany.name,
      contactPersonName: this.paymentInfo && this.paymentInfo.userName || '',
      email: this.paymentInfo && this.paymentInfo.userEmail || ''
    } as IInvoiceInfo;
  }

  private _saveInvoiceInfo(invoiceInfo): Observable<IInvoiceInfo> {
    return this._manager.saveInvoiceInfo(invoiceInfo);
  }
}
